/**
 * Handing booking context to the Nuitee popup without putting it on the URL.
 *
 * The hotel and flight popups need a lot of context: the trip, the sector, every passenger on the
 * booking with their date of birth, identity documents and loyalty numbers, a previously saved
 * option, the traveller's accessibility requirements. All of it used to travel as query
 * parameters, and that caused two separate production bugs in one week:
 *
 *   • a parameter named `origin` (where the popup was opened from) silently overwrote `origin`
 *     (the departure airport), blanking the prefilled search;
 *   • the URL grew past 2048 characters, which the LiteAPI payment wrapper rejects outright —
 *     and it does so at the pay button, after the fare is already held.
 *
 * Both are the same underlying problem: a flat, unbounded, untyped namespace. The context is now
 * stashed under a short random handle and only the handle travels on the URL.
 *
 * ⚠️ localStorage, not sessionStorage. A popup does get a *copy* of its opener's sessionStorage,
 * but only at creation — reusing an already-open booking window (which both flows do, to avoid
 * popup blockers) would read a stale copy. localStorage is shared live across windows of the same
 * origin.
 *
 * ⚠️ This holds passport numbers and dates of birth, so it is deleted the moment the popup reads
 * it, and anything left behind by a popup that never opened is swept on the next write.
 */

/**
 * The only keys that belong on the URL.
 *
 * They have to survive a reload: the payment wrapper navigates the window and returns to a URL
 * built from these, by which point the stashed context has already been consumed. Shared with
 * paymentReturnUrl so the two cannot drift — a key dropped from one and not the other leaves the
 * popup coming back from payment not knowing which trip it belongs to.
 */
export const BOOKING_URL_KEYS = ['tripId', 'sectorIndex', 'clientId', 'mode', 'openedFrom', 'tripType'];

const PREFIX  = 'stx_booking_ctx_';
const MAX_AGE = 60 * 60 * 1000;   // an hour is far longer than any real hand-off

/** Remove anything a popup never collected, so PII does not accumulate. */
function sweep(now) {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(PREFIX)) continue;
      let at = 0;
      try { at = JSON.parse(localStorage.getItem(key))?.at || 0; } catch { /* corrupt — drop it */ }
      if (!at || now - at > MAX_AGE) localStorage.removeItem(key);
    }
  } catch { /* storage unavailable; nothing to sweep */ }
}

/**
 * Stash the context and return a handle, or null when storage is unavailable (private browsing,
 * quota). A null handle is not an error — the caller falls back to the URL, which still works for
 * everything except an unusually large passenger list.
 */
export function stashBookingContext(context) {
  const now = Date.now();
  sweep(now);
  const handle = `bk_${now.toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  try {
    localStorage.setItem(PREFIX + handle, JSON.stringify({ at: now, context }));
    // Read back: some browsers accept the write and discard it.
    if (!localStorage.getItem(PREFIX + handle)) return null;
    return handle;
  } catch {
    return null;
  }
}

/**
 * Collect the context for a handle and delete it. Returns null for an unknown or expired handle,
 * which is how a reloaded popup ends up falling back to the URL rather than throwing.
 */
export function takeBookingContext(handle) {
  if (!handle) return null;
  const key = PREFIX + handle;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    localStorage.removeItem(key);
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.at || Date.now() - parsed.at > MAX_AGE) return null;
    return parsed.context || null;
  } catch {
    try { localStorage.removeItem(key); } catch { /* best effort */ }
    return null;
  }
}

/**
 * The popup's reader: prefer the stashed context, fall back to the query string.
 *
 * The fallback is what keeps a link built before this existed — or one opened when storage was
 * blocked — working exactly as it did.
 */
export function bookingParamReader(search = window.location.search) {
  const params  = new URLSearchParams(search);
  const context = takeBookingContext(params.get('ctx')) || {};
  return (key, fallback = '') => {
    const fromContext = context[key];
    if (fromContext !== undefined && fromContext !== null) return fromContext;
    const fromUrl = params.get(key);
    return fromUrl === null ? fallback : fromUrl;
  };
}

/**
 * Build the popup URL. Short keys stay on the query string so the window is recognisable and a
 * reload still knows which trip it belongs to; everything bulky goes to storage.
 *
 * `urlKeys` are the ones that must survive a reload — the payment redirect returns to a URL built
 * from these, and the stashed context will already have been consumed by then.
 */
export function buildBookingUrl(path, contextLike, urlKeys) {
  // The openers already build a URLSearchParams; take either that or a plain object so none of
  // them has to be restructured just to move the payload off the URL.
  const context = contextLike instanceof URLSearchParams
    ? Object.fromEntries(contextLike.entries())
    : (contextLike || {});
  const handle = stashBookingContext(context);
  const params = new URLSearchParams();
  for (const key of urlKeys) {
    const value = context[key];
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  if (handle) {
    params.set('ctx', handle);
  } else {
    // Storage refused us — fall back to the old behaviour rather than opening a popup that knows
    // nothing. Long, but functional for everything bar an unusually large passenger list.
    for (const [key, value] of Object.entries(context)) {
      if (value === undefined || value === null || value === '') continue;
      if (params.has(key)) continue;
      params.set(key, typeof value === 'string' ? value : JSON.stringify(value));
    }
  }
  return `${path}?${params.toString()}`;
}
